// _sys/blocks/primitives/PricingTierGrid.tsx
"use client";

import React from "react";
import styled from "styled-components";
import type { PricingTier, PricingTiersBlock } from "../../_sys/_types";
import { useScrollReveal } from "../hooks/useScrollReveal";
import { getIcon } from "../blocks/shared";

const Wrapper = styled.div`
  margin: ${({ theme }) => theme.spacing.large} 0;
`;

const BlockTitle = styled.h4`
  font-family: ${({ theme }) => theme.typography.fonts.semibold};
  font-size: ${({ theme }) => theme.typography.sizes.title};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 ${({ theme }) => theme.spacing.medium} 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing.medium};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const TierCard = styled.div<{ $featured: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing.medium};
  border-radius: ${({ theme }) => theme.borders.radius.medium};
  background: ${({ theme, $featured }) =>
    $featured ? theme.gradients.glass : theme.colors.primaryBackground};
  border: ${({ theme, $featured }) =>
    $featured
      ? `${theme.borders.width.medium} dashed ${theme.colors.primary}`
      : `${theme.borders.width.thin} solid ${theme.colors.secondaryBackground}`};
  transition: transform ${({ theme }) => theme.animations.duration.short}
    ${({ theme }) => theme.animations.easing.easeOut};

  &:hover {
    transform: translateY(-3px);
  }
`;

const FeaturedFlag = styled.div`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: ${({ theme }) => theme.gradients.strong};
  color: ${({ theme }) => theme.colors.static.white};
  font-family: ${({ theme }) => theme.typography.fonts.bold};
  font-size: ${({ theme }) => theme.typography.sizes.tab};
  padding: 2px ${({ theme }) => theme.spacing.small};
  border-radius: ${({ theme }) => theme.borders.radius.rounded};
  text-transform: uppercase;
  letter-spacing: ${({ theme }) => theme.typography.letterSpacing.body};
`;

const TierName = styled.div`
  font-family: ${({ theme }) => theme.typography.fonts.semibold};
  font-size: ${({ theme }) => theme.typography.sizes.subject};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: ${({ theme }) => theme.spacing.tight};
`;

const TierPriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
  margin-bottom: ${({ theme }) => theme.spacing.small};
`;

const TierPrice = styled.div`
  font-family: ${({ theme }) => theme.typography.fonts.numbers};
  font-size: ${({ theme }) => theme.typography.sizes.header};
  font-weight: ${({ theme }) => theme.typography.weights.header};
  color: ${({ theme }) => theme.colors.primary};
`;

const TierPriceSuffix = styled.div`
  font-family: ${({ theme }) => theme.typography.fonts.regular};
  font-size: ${({ theme }) => theme.typography.sizes.hint};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const TierQuantity = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: ${({ theme }) => theme.typography.fonts.medium};
  font-size: ${({ theme }) => theme.typography.sizes.body};
  color: ${({ theme }) => theme.colors.textPrimary};

  svg {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const TierDescription = styled.div`
  font-family: ${({ theme }) => theme.typography.fonts.regular};
  font-size: ${({ theme }) => theme.typography.sizes.hint};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 2px;
`;

const TierCTA = styled.a`
  display: inline-block;
  margin-top: ${({ theme }) => theme.spacing.medium};
  padding: ${({ theme }) => theme.spacing.tight}
    ${({ theme }) => theme.spacing.medium};
  border-radius: ${({ theme }) => theme.borders.radius.small};
  text-align: center;
  text-decoration: none;
  font-family: ${({ theme }) => theme.typography.fonts.semibold};
  font-size: ${({ theme }) => theme.typography.sizes.hint};
  background: ${({ theme }) => theme.gradients.strong};
  color: ${({ theme }) => theme.colors.static.white};
  transition: transform ${({ theme }) => theme.animations.duration.short}
    ${({ theme }) => theme.animations.easing.easeOut};

  &:hover {
    transform: translateY(-2px);
  }
`;

const Disclaimer = styled.p`
  font-family: ${({ theme }) => theme.typography.fonts.regularItalic};
  font-size: ${({ theme }) => theme.typography.sizes.hint};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: ${({ theme }) => theme.spacing.medium} 0 0 0;
  text-align: right;
`;

export const PricingTierGrid: React.FC<{
  tiers: PricingTier[];
  title?: string;
  disclaimer?: string;
}> = ({ tiers, title, disclaimer }) => {
  const containerRef = useScrollReveal();
  const CoinIcon = getIcon("package");

  return (
    <Wrapper>
      {title && <BlockTitle>{title}</BlockTitle>}
      <Grid ref={containerRef}>
        {tiers.map((tier) => (
          <TierCard key={tier.id} $featured={!!tier.featured}>
            {tier.featured && <FeaturedFlag>Popular</FeaturedFlag>}
            <TierName>{tier.name}</TierName>
            <TierPriceRow>
              <TierPrice>{tier.price}</TierPrice>
              {tier.priceSuffix && (
                <TierPriceSuffix>{tier.priceSuffix}</TierPriceSuffix>
              )}
            </TierPriceRow>
            {tier.quantityLabel && (
              <TierQuantity>
                <CoinIcon size={16} weight="bold" />
                {tier.quantityLabel}
              </TierQuantity>
            )}
            {tier.description && (
              <TierDescription>{tier.description}</TierDescription>
            )}
            {tier.ctaLabel && tier.ctaUrl && (
              <TierCTA href={tier.ctaUrl}>{tier.ctaLabel}</TierCTA>
            )}
          </TierCard>
        ))}
      </Grid>
      {disclaimer && <Disclaimer>{disclaimer}</Disclaimer>}
    </Wrapper>
  );
};

export const PricingTierGridFromBlock: React.FC<{
  block: PricingTiersBlock;
}> = ({ block }) => (
  <PricingTierGrid
    tiers={block.tiers}
    title={block.title}
    disclaimer={block.disclaimer}
  />
);
